import { TextBase } from '@carboncopyco/ui';
import { capitalize } from '@carboncopyco/utils';
import { RadioGroup } from '@headlessui/react';
import classNames from 'classnames';
import type { ReactNode } from 'react';

type SideTabsItem<T extends string | number> = {
  value: T;
  label: string;
  icon: ReactNode;
  displayLabel?: string;
};

export interface SideTabsProps<T extends string | number> {
  items: SideTabsItem<T>[];
  currentValue: T;
  onChange: (value: T) => void;
  extraSidebarContent?: ReactNode;
  children: ReactNode;
}

export const SideTabs = <T extends string | number>({
  items,
  currentValue,
  onChange,
  extraSidebarContent,
  children,
}: SideTabsProps<T>) => (
  <div className="flex grow min-w-0 min-h-0">
    <div className="flex flex-col w-60 pt-5">
      <RadioGroup
        value={currentValue}
        onChange={onChange}
        className="h-full overflow-y-auto"
      >
        <div className="flex flex-col gap-3">
          {items.map(({ value, label, icon, displayLabel }) => {
            return (
              <RadioGroup.Option
                key={value}
                value={value}
              >
                {({ checked }) => (
                  <div
                    className={classNames(
                      'flex items-center w-full h-11  text-ds-primary-300  hover:cursor-pointer whitespace-nowrap',
                      {
                        'border-l-3 border-ds-primary-300 bg-ds-primary-50':
                          checked,
                      },
                      {
                        'hover:bg-ds-natural-50 pl-[3px]': !checked,
                      }
                    )}
                  >
                    {checked && null}
                    <div
                      className={classNames(
                        'flex items-center px-5 py-3 min-w-0',
                        checked ? 'text-ds-primary-300' : 'text-ds-natural-600'
                      )}
                    >
                      <div className="mr-1.5">{icon}</div>
                      <TextBase
                        ellipsis
                        label={capitalize(displayLabel ?? label)}
                        color={
                          checked
                            ? 'text-ds-primary-300'
                            : 'text-ds-natural-600'
                        }
                      />
                    </div>
                  </div>
                )}
              </RadioGroup.Option>
            );
          })}
        </div>
      </RadioGroup>
      {extraSidebarContent && (
        <div className="mt-auto">{extraSidebarContent}</div>
      )}
    </div>
    <div className="flex flex-1 min-w-0 border-l border-l-ds-natural-150">
      {children}
    </div>
  </div>
);

export const SideTabsPanel = <T extends string | number>({
  value,
  currentValue,
  children,
}: {
  value: T;
  currentValue: T;
  children: ReactNode;
}) => {
  if (value !== currentValue) {
    return null;
  }
  return <>{children}</>;
};
